import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, CircularProgress, Modal } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import XIconSvg from "../../../../common/svgs/XIconSvg";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import { useAppStore } from "../../../../providers/AppStore";
import PrimaryButton from "../../../../common/ui/PrimaryButton";
import SecondaryButton from "../../../../common/ui/SecondaryButton";
import ScrollableWrapper from "../common/ScrollableWrapper";
import CustomSelect from "../common/CustomSelect";
import StepProgressBar from "../common/StepProgressBar";
import KeragonClientCard from "./KeragonClientCard";
import UnsavedBiomarkersNotice, {
  unsavedNoticeMessage,
} from "../biomarkerTab/UnsavedBiomarkersNotice";
import { NormalizedBiomarker } from "../../utils/extractorResults";
import { platformActions, RootStatePlatform } from "../../store/platform-slice";
import {
  getKeragonResultStatus,
  getPlatformData,
  KeragonTriggerError,
  triggerKeragonWorkflow,
  UpdateCostumerBiomarkersBatch,
} from "../../../../common/network/Api";
import { KeragonPatient } from "../../../../common/entities/keragon";
import { UpdateBiomarkerBatchItem } from "../../../../common/entities/type";
import { useAuth } from "../../../../providers/AuthProvider";
import { parseRangeString } from "../../store/helper";
import { useBiomarkersSavedSnackbar } from "../../hooks/useBiomarkersSavedSnackbar";
import { KeragonPollError, pollKeragonResult } from "./keragonPoll";
import { formatDisplayDate } from "../../../../common/utils/date";
import { buildCatalogById } from "../../utils/extractorResults";
import KeragonDateRangeChips from "./KeragonDateRangeChips";
import KeragonViewToggle, { KeragonView } from "./KeragonViewToggle";
import KeragonGroupCard from "./KeragonGroupCard";
import {
  DATE_RANGE_PRESETS,
  CustomRange,
  DateRangePreset,
  isCustomRangeInverted,
  resolveRange,
  toSince,
} from "./keragonDateRange";
import {
  buildRows,
  existingKey,
  filterRowsByRange,
  formatRowValue,
  groupByBiomarker,
  groupByDate,
  groupCheckState,
  KeragonRow,
  selectAll,
  splitResultByDate,
  toggleGroup,
  toggleRow,
} from "./keragonSelection";

/**
 * "Sync EHR data" — pull a client's lab results from the affiliate's connected
 * Keragon workflow (Figma 16758:469482 → 16753:468272 / 16772:502625).
 *
 * Step 1: pick the system (defaults to the workflow marked default in
 * Settings) and how far back to search. Search POSTs the client's identity +
 * `since` to the workflow's Keragon signal URL, then polls KeragonResult/status
 * with the same identity until the result arrives (keragonPoll). Step 2:
 * review every reading found, grouped by biomarker or by lab date, and tick
 * what to import; Import saves through UpdateBiomarkers with each reading's
 * Keragon id as `batchId`.
 *
 * Same shell as InBodySyncModal so the two sync flows feel identical.
 */
type Props = {
  open: boolean;
  onClose: () => void;
  // Opens the host's Review health scores modal: imported biomarkers move
  // system scores into "pending approval".
  onReviewScores?: () => void;
};

const TOTAL_STEPS = 2;
const EMPTY_RANGE: CustomRange = { startDate: null, endDate: null };
// "Last 6 months" opens preselected (user decision; Figma showed "Last year").
const DEFAULT_RANGE_PRESET: DateRangePreset = "6m";

const KeragonSyncModal = ({ open, onClose, onReviewScores }: Props) => {
  const theme: Theme = useTheme();
  const {
    ASSelectedClient,
    ASKeragonWorkflows,
    ASLoadKeragonWorkflows,
    ASSetOpenSnackBar,
    ASSetSnackBarMessage,
    ASSetTypeSnackBar,
    ASGetClientBiomarkerTabDataHandler,
  } = useAppStore();
  const { AuthUser } = useAuth();
  const dispatch = useDispatch();
  const showSavedSnackbar = useBiomarkersSavedSnackbar(onReviewScores);
  const biomarkerList = useSelector(
    (state: RootStatePlatform) => state.platform.biomarkerList
  );
  const biomarkerData = useSelector(
    (state: RootStatePlatform) => state.platform.biomarkerData
  );

  const [step, setStep] = useState(1);
  const [workflowId, setWorkflowId] = useState<string | null>(null);
  const [preset, setPreset] = useState<DateRangePreset>(DEFAULT_RANGE_PRESET);
  const [custom, setCustom] = useState<CustomRange>(EMPTY_RANGE);
  const [isSearching, setIsSearching] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  // Set on close/unmount so an in-flight poll stops and drops its result.
  const pollCancelledRef = useRef(false);
  const [rows, setRows] = useState<KeragonRow[]>([]);
  const [skipped, setSkipped] = useState<{
    unmatched: NormalizedBiomarker[];
    rejected: NormalizedBiomarker[];
  }>({ unmatched: [], rejected: [] });
  const [view, setView] = useState<KeragonView>("biomarkers");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const customerId = ASSelectedClient?.id ?? "";
  // The workspace's selected client is built by getCustomerPersonalInfoHandler
  // with camel-case names; the lower-case variants exist on other UserType
  // shapes, so accept both.
  const clientName =
    ASSelectedClient?.name ||
    [
      ASSelectedClient?.firstName ?? ASSelectedClient?.firstname,
      ASSelectedClient?.lastName ?? ASSelectedClient?.lastname,
    ]
      .filter(Boolean)
      .join(" ");
  const spinnerColor = theme.colors_ds.NEUTRAL_TEXT_WEAK;

  // Keragon correlates the trigger and the poll by these three fields, so
  // build them once and send the very same object to both.
  const patient: KeragonPatient | null = useMemo(() => {
    const email = (ASSelectedClient?.email ?? "").trim();
    const first = String(ASSelectedClient?.firstName ?? ASSelectedClient?.firstname ?? "").trim();
    const last = String(ASSelectedClient?.lastName ?? ASSelectedClient?.lastname ?? "").trim();
    return email && first && last ? { email, first_name: first, last_name: last } : null;
  }, [ASSelectedClient]);

  const defaultWorkflowId =
    ASKeragonWorkflows.find((w) => w.active === 1)?.id ??
    ASKeragonWorkflows[0]?.id ??
    null;

  // Fresh wizard each time it opens, preselecting the default workflow.
  useEffect(() => {
    pollCancelledRef.current = !open;
    if (open) {
      ASLoadKeragonWorkflows();
      setStep(1);
      setIsSearching(false);
      setIsImporting(false);
      setWorkflowId(defaultWorkflowId);
      setPreset(DEFAULT_RANGE_PRESET);
      setCustom(EMPTY_RANGE);
      setRows([]);
      setSkipped({ unmatched: [], rejected: [] });
      setView("biomarkers");
      setSelected(new Set());
      setExpanded(new Set());
    }
    // Reset only on open — not when the workflow list refreshes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(
    () => () => {
      pollCancelledRef.current = true;
    },
    []
  );

  // If the list arrived after opening, adopt the default once.
  useEffect(() => {
    if (open && !workflowId && defaultWorkflowId) setWorkflowId(defaultWorkflowId);
  }, [open, workflowId, defaultWorkflowId]);

  const catalogById = useMemo(() => buildCatalogById(biomarkerList), [biomarkerList]);

  // Readings this client already has, keyed biomarker|date, to mark what is new.
  const existingKeys = useMemo(() => {
    const keys = new Set<string>();
    Object.values(biomarkerData ?? {}).forEach((items) =>
      (items ?? []).forEach((item) =>
        (item.values ?? []).forEach((entry) => {
          if (entry?.dateCreated) keys.add(existingKey(item.biomarkerID, entry.dateCreated));
        })
      )
    );
    return keys;
  }, [biomarkerData]);

  const notify = (title: string, message: string, type: string) => {
    ASSetSnackBarMessage({ title, message });
    ASSetTypeSnackBar(type);
    ASSetOpenSnackBar(true);
  };

  const rangeInverted = preset === "custom" && isCustomRangeInverted(custom);
  const selectedWorkflow = ASKeragonWorkflows.find((w) => w.id === workflowId);

  // Copy for the search outcomes names the system that was asked and what to
  // try next: another connected system when the company has one, otherwise
  // the Settings page where one is added or the webhook URL fixed.
  const systemName = selectedWorkflow?.name?.trim()
    ? `"${selectedWorkflow.name.trim()}"`
    : "The connected system";
  const otherSystems = ASKeragonWorkflows.filter(
    (w) => w.id !== workflowId && w.active === 1 && Boolean(w.URL)
  ).length;
  const nextSystemHint =
    otherSystems > 0
      ? `pick another connected system from the System list${
          otherSystems === 1 ? "" : ` (${otherSystems} more available)`
        }`
      : "add another connected system in Settings → Keragon sync";
  const rangeLabel =
    preset === "custom"
      ? "the selected custom range"
      : (
          DATE_RANGE_PRESETS.find((option) => option.key === preset)?.label ??
          "this date range"
        ).toLowerCase();
  const canSearch =
    Boolean(selectedWorkflow?.URL) && Boolean(patient) && !rangeInverted && !isSearching;

  const handleSearch = async () => {
    if (!selectedWorkflow?.URL || !patient) return;
    setIsSearching(true);
    pollCancelledRef.current = false;
    try {
      const range = resolveRange(preset, custom);
      const triggeredAt = Date.now();
      await triggerKeragonWorkflow(selectedWorkflow.URL, {
        ...patient,
        since: toSince(range),
      });
      const result = await pollKeragonResult({
        fetchStatus: () => getKeragonResultStatus(patient),
        triggeredAt,
        isCancelled: () => pollCancelledRef.current,
      });
      const built = buildRows(splitResultByDate(result), catalogById, existingKeys);
      // The trigger only takes `since`; apply the end of the range here.
      const inRange = filterRowsByRange(built.rows, range);
      // Nothing importable: stay on step 1 so the affiliate can widen the
      // range or pick another system, and say why in the snackbar. The
      // workflow itself worked, so the copy says so and names what to change:
      // the range, the system, or the client's identity in the EHR (records
      // are matched by the client's email).
      if (inRange.length === 0) {
        const skippedCount = built.unmatchedCount + built.rejectedCount;
        notify(
          "No results to import",
          skippedCount > 0
            ? `${systemName} returned ${skippedCount} result${
                skippedCount === 1 ? "" : "s"
              } for ${rangeLabel}, but ${
                skippedCount === 1 ? "it" : "they"
              } couldn't be matched to our biomarker database or converted to our units.`
            : `${systemName} ran successfully but has no records for ${
                patient.email
              } in ${rangeLabel}. Try a wider date range, ${nextSystemHint}, or check that this email is the one on the client's EHR record.`,
          "alert"
        );
        return;
      }
      setRows(inRange);
      setSkipped({ unmatched: built.unmatchedItems, rejected: built.rejectedItems });
      setSelected(selectAll(inRange));
      // Every group opens expanded in both views. Biomarker ids and dates
      // never collide, so one set holds the keys of both.
      setExpanded(
        new Set(inRange.flatMap((row) => [row.biomarkerID, row.date]))
      );
      setView("biomarkers");
      setStep(2);
    } catch (error) {
      if (error instanceof KeragonPollError && error.reason === "cancelled") return;
      console.error("Error searching Keragon records:", error);
      const proxyRejected = (error as any)?.response?.status === 400;
      if (error instanceof KeragonTriggerError || proxyRejected) {
        notify(
          "Search failed",
          `${systemName} rejected the request${
            error instanceof KeragonTriggerError && error.status ? ` (status ${error.status})` : ""
          }. Check its webhook URL in Settings → Keragon sync, or ${nextSystemHint}.`,
          "alert"
        );
      } else if (error instanceof KeragonPollError && error.reason === "timeout") {
        notify(
          "No response from the EHR",
          `${systemName} accepted the request but didn't return results in time. Try again in a moment, or ${nextSystemHint}.`,
          "alert"
        );
      } else if (error instanceof KeragonPollError) {
        notify("Search failed", error.message, "alert");
      } else {
        notify(
          "Search failed",
          `We couldn't reach ${systemName} to search this client's records. Try again, ${nextSystemHint}, or check its webhook URL in Settings → Keragon sync.`,
          "alert"
        );
      }
    } finally {
      if (!pollCancelledRef.current) setIsSearching(false);
    }
  };

  const handleImport = async () => {
    const chosen = rows.filter((r) => selected.has(r.rowKey));
    if (!customerId || chosen.length === 0) return;
    setIsImporting(true);
    try {
      // Same payload as the extractor's save (values are already in our
      // canonical unit), dated per reading, plus the EHR id as batchId.
      const payload: UpdateBiomarkerBatchItem[] = chosen.map((row) => {
        const base: UpdateBiomarkerBatchItem = {
          biomarkerId: row.biomarkerID,
          userId: customerId,
          date: row.date,
          ...(row.keragonId ? { batchId: row.keragonId } : {}),
        };
        if (row.status === "qualitative") return { ...base, qvalue: row.displayValue };
        if (row.status === "range") return { ...base, ...parseRangeString(row.displayValue) };
        return { ...base, newValue: Number(row.displayValue) };
      });
      await UpdateCostumerBiomarkersBatch(payload);

      const [, dataPlatform] = await Promise.all([
        ASGetClientBiomarkerTabDataHandler(customerId),
        getPlatformData(customerId, AuthUser.role),
      ]);
      dispatch(platformActions.updateDataPlatformServer(dataPlatform));
      setIsImporting(false);
      onClose();
      showSavedSnackbar({
        report: dataPlatform,
        reviewTitle: "Biomarkers imported",
        fallback: {
          title: "",
          message: "The EHR lab results have been imported successfully.",
        },
      });
    } catch (error) {
      console.error("Error importing Keragon records:", error);
      notify("Import failed", "We couldn't save the selected results. Please try again.", "alert");
      setIsImporting(false);
    }
  };

  const toggleExpand = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const allSelected = rows.length > 0 && selected.size === rows.length;
  const biomarkerGroups = useMemo(() => groupByBiomarker(rows), [rows]);
  const dateGroups = useMemo(() => groupByDate(rows), [rows]);

  const summary =
    view === "biomarkers"
      ? `${biomarkerGroups.length} biomarker${biomarkerGroups.length === 1 ? "" : "s"} · ${selected.size} of ${rows.length} values selected`
      : `${dateGroups.length} lab date${dateGroups.length === 1 ? "" : "s"} · ${selected.size} of ${rows.length} values selected`;

  const toNoticeItems = (items: NormalizedBiomarker[]) =>
    items.map((item) => ({
      name: item.displayName,
      value: item.displayValue,
      unit: item.displayUnit,
    }));

  // Same cards as the upload extractor. They lead the scrollable list so a
  // long list of skipped readings never squeezes the groups off screen.
  const renderSkippedNotices = () => {
    const notices = [
      {
        key: "rejected",
        title: "Couldn’t convert units",
        reason: "we couldn’t convert to the units we use",
        items: skipped.rejected,
      },
      {
        key: "unmatched",
        title: "Not matched to our database",
        reason: "we couldn’t match to our database",
        items: skipped.unmatched,
      },
    ].filter((n) => n.items.length > 0);
    return notices.map((n) => (
      <UnsavedBiomarkersNotice
        key={n.key}
        title={n.title}
        message={unsavedNoticeMessage(n.items.length, n.reason, "imported")}
        items={toNoticeItems(n.items)}
        style={{ marginTop: 0 }}
      />
    ));
  };

  const workflowOptions = ASKeragonWorkflows.map((w) => ({ title: w.name, value: w.id }));

  const renderGroups = () => {
    if (rows.length === 0) {
      return (
        <Box sx={styles.emptyState(theme)}>
          No results found in this range. Go back to widen the search.
        </Box>
      );
    }
    if (view === "biomarkers") {
      return biomarkerGroups.map((group) => {
        const keys = group.rows.map((r) => r.rowKey);
        return (
          <KeragonGroupCard
            key={group.biomarkerID}
            title={group.name}
            // Same shape as the Labs view header: the group name over a count.
            meta={`${group.rows.length} record${group.rows.length === 1 ? "" : "s"}`}
            checkState={groupCheckState(selected, keys)}
            expanded={expanded.has(group.biomarkerID)}
            onToggle={() => setSelected((prev) => toggleGroup(prev, keys))}
            onToggleExpand={() => toggleExpand(group.biomarkerID)}
            columns={["Date", "Value"]}
            rows={group.rows.map((row) => ({
              key: row.rowKey,
              left: formatDisplayDate(row.date),
              right: formatRowValue(row),
              checked: selected.has(row.rowKey),
            }))}
            onToggleRow={(key) => setSelected((prev) => toggleRow(prev, key))}
          />
        );
      });
    }
    return dateGroups.map((group) => {
      const keys = group.rows.map((r) => r.rowKey);
      return (
        <KeragonGroupCard
          key={group.date}
          title={formatDisplayDate(group.date)}
          meta={`${group.rows.length} biomarker${group.rows.length === 1 ? "" : "s"}`}
          checkState={groupCheckState(selected, keys)}
          expanded={expanded.has(group.date)}
          onToggle={() => setSelected((prev) => toggleGroup(prev, keys))}
          onToggleExpand={() => toggleExpand(group.date)}
          columns={["Name", "Value"]}
          rows={group.rows.map((row) => ({
            key: row.rowKey,
            left: row.displayName,
            right: formatRowValue(row),
            checked: selected.has(row.rowKey),
          }))}
          onToggleRow={(key) => setSelected((prev) => toggleRow(prev, key))}
        />
      );
    });
  };

  return (
    <Modal open={open} onClose={onClose} disableEnforceFocus>
      <Box sx={styles.modalContainer(theme)}>
        {/* Header */}
        <Box sx={styles.header}>
          <Box sx={styles.title(theme)}>Sync EHR data</Box>
          <Box onClick={onClose} sx={styles.iconWrapper}>
            <XIconSvg />
          </Box>
        </Box>

        <StepProgressBar step={step} total={TOTAL_STEPS} />

        {step === 1 ? (
          /* Step 1 — system + date range */
          <Box sx={styles.body}>
            <ScrollableWrapper customPadding="0">
              <Box sx={styles.stepContent}>
                <KeragonClientCard
                  name={clientName}
                  email={ASSelectedClient?.email}
                />
                <Box>
                  <Box sx={styles.stepHeading(theme)}>Choose system and date range</Box>
                  <Box sx={styles.stepDescription(theme)}>
                    Pick the connected system and how far back to search. Nothing
                    is written back to the EHR.
                  </Box>
                </Box>
                <Box sx={styles.fields}>
                  <CustomSelect
                    label="System"
                    options={workflowOptions}
                    value={workflowId}
                    onChange={(value) => setWorkflowId(value)}
                    placeholder="Select a connected system"
                  />
                  <KeragonDateRangeChips
                    preset={preset}
                    onPresetChange={setPreset}
                    custom={custom}
                    onCustomChange={setCustom}
                    disabled={isSearching}
                  />
                </Box>
                {!patient && (
                  <Box sx={styles.errorText(theme)}>
                    This client needs an email, first name and last name to be
                    matched in the EHR.
                  </Box>
                )}
                {rangeInverted && (
                  <Box sx={styles.errorText(theme)}>
                    The start date must be before the end date.
                  </Box>
                )}
              </Box>
            </ScrollableWrapper>
          </Box>
        ) : (
          /* Step 2 — choose what to import */
          <Box sx={styles.body}>
            <Box sx={styles.stepTop}>
              <Box>
                <Box sx={styles.stepHeading(theme)}>Choose what to import</Box>
                <Box sx={styles.stepDescription(theme)}>
                  Every reading found in the selected period. Values already in
                  PNOE are marked as not new.
                </Box>
              </Box>
              <KeragonViewToggle value={view} onChange={setView} />
              <Box sx={styles.selectRow}>
                <Box sx={styles.selectedCount(theme)}>{summary}</Box>
                {rows.length > 0 && (
                  <Box
                    sx={styles.linkAction(theme)}
                    onClick={() =>
                      setSelected(allSelected ? new Set() : selectAll(rows))
                    }
                  >
                    {allSelected ? "Deselect all" : "Select all"}
                  </Box>
                )}
              </Box>
            </Box>
            <Box sx={styles.listArea}>
              <ScrollableWrapper customPadding="0">
                <Box sx={styles.groupList}>
                  {renderSkippedNotices()}
                  {renderGroups()}
                </Box>
              </ScrollableWrapper>
            </Box>
          </Box>
        )}

        {/* Footer */}
        <Box sx={styles.footer}>
          {step === 1 ? (
            <PrimaryButton
              label={isSearching ? "Searching…" : "Search records"}
              onClick={handleSearch}
              isDisable={!canSearch}
              icon={
                isSearching ? (
                  <CircularProgress size={16} sx={{ color: spinnerColor }} />
                ) : undefined
              }
              sx={{ justifyContent: "center", width: "auto" }}
              customLabelStyles={{ flex: "none" }}
            />
          ) : (
            <>
              <SecondaryButton
                label="Back"
                onClick={() => setStep(1)}
                isDisable={isImporting}
              />
              <PrimaryButton
                label={isImporting ? "Importing…" : "Import"}
                onClick={handleImport}
                isDisable={selected.size === 0 || isImporting}
                icon={
                  isImporting ? (
                    <CircularProgress size={16} sx={{ color: spinnerColor }} />
                  ) : undefined
                }
                sx={{ justifyContent: "center", width: "auto" }}
                customLabelStyles={{ flex: "none" }}
              />
            </>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default KeragonSyncModal;

const styles = {
  modalContainer: (theme: Theme) => ({
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    position: "absolute" as "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    padding: "16px",
    width: "80%",
    minWidth: "400px",
    maxWidth: "854px",
    height: "90vh",
    borderRadius: "8px",
    display: "flex",
    flexDirection: "column" as "column",
    gap: "24px",
  }),
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  title: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Medium)",
    fontSize: "24px",
    lineHeight: "34px",
    flex: 1,
  }),
  iconWrapper: {
    cursor: "pointer",
    padding: "8px",
    display: "flex",
  },
  body: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "16px",
    flex: 1,
    minHeight: 0,
    overflow: "hidden",
  },
  stepContent: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "24px",
    paddingBottom: "8px",
  },
  // The select and the date-range chips are one form under one heading.
  fields: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "16px",
  },
  stepTop: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "16px",
    flexShrink: 0,
  },
  stepHeading: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "20px",
    color: theme.colorTokens.N[90],
    marginBottom: "4px",
  }),
  stepDescription: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "17px",
    letterSpacing: "-0.24px",
    color: theme.colorTokens.N[80],
  }),
  errorText: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    color: theme.colors_ds.DANGER_GRAPH_DEFICIT,
  }),
  selectRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
  },
  selectedCount: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "17px",
    letterSpacing: "-0.24px",
    color: theme.colorTokens.N[80],
  }),
  linkAction: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "17px",
    color: theme.colors_ds.PRIMARY_TEXT_MAIN,
    cursor: "pointer",
    whiteSpace: "nowrap" as "nowrap",
  }),
  listArea: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column" as "column",
  },
  groupList: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "8px",
    paddingBottom: "8px",
  },
  emptyState: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "17px",
    color: theme.colorTokens.N[80],
    padding: "24px 0",
    textAlign: "center" as "center",
  }),
  footer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "16px",
    flexShrink: 0,
  },
};
